import React, { useCallback, useEffect, useState } from 'react';
import {
  isNativeApp,
  nativeListDevices,
  nativeRequestPermission,
  NativeUsbDevice,
} from '../services/nativeUsb';
import { getSavedPrinter, savePrinter, getUsb } from '../services/usbPrinter';
import './UsbPrinterPrompt.css';

// App-open USB printer chooser. On the Android app it lists the connected USB
// devices via the native UsbPrinter plugin; picking one triggers the system
// USB-permission dialog and saves it as the default receipt printer (bills
// then print straight to it — see services/receipt.ts). In the browser it
// falls back to the WebUSB device picker. Skipping asks again next launch;
// once a printer is saved the prompt no longer appears (it only re-requests
// the Android permission, which lapses when the printer is unplugged).

const SKIP_KEY = 'sconnect.usbPromptSkipped';

const UsbPrinterPrompt: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [devices, setDevices] = useState<NativeUsbDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const native = isNativeApp();

  const refresh = useCallback(async () => {
    try {
      setDevices(await nativeListDevices());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(SKIP_KEY)) return;
    const saved = getSavedPrinter();
    if (native) {
      (async () => {
        try {
          const list = await nativeListDevices();
          if (saved) {
            // Printer already chosen — just re-arm the Android USB permission
            // (it resets when the printer is unplugged or the app reinstalled).
            const d = list.find(
              (x) => x.vendorId === saved.vendorId && x.productId === saved.productId
            );
            if (d && !d.hasPermission) await nativeRequestPermission(d.vendorId, d.productId);
            return;
          }
          setDevices(list);
          setVisible(true);
        } catch (e) {
          console.warn('USB printer prompt failed:', e);
        }
      })();
    } else if (getUsb() && !saved) {
      setVisible(true);
    }
  }, [native]);

  const close = (skip: boolean) => {
    if (skip) sessionStorage.setItem(SKIP_KEY, '1');
    setVisible(false);
  };

  const pickNative = async (d: NativeUsbDevice) => {
    setError(null);
    try {
      const granted = await nativeRequestPermission(d.vendorId, d.productId);
      if (!granted) {
        setError('USB permission was denied for that device.');
        return;
      }
      savePrinter({
        vendorId: d.vendorId,
        productId: d.productId,
        label: d.productName || 'USB Printer',
      });
      close(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const pickWeb = async () => {
    setError(null);
    const usb = getUsb();
    if (!usb) return;
    try {
      const d = await usb.requestDevice({ filters: [] });
      savePrinter({
        vendorId: d.vendorId,
        productId: d.productId,
        label: d.productName || 'USB Printer',
      });
      close(false);
    } catch (e) {
      const msg = (e as Error).message || '';
      if (!/No device selected/i.test(msg)) setError(msg);
    }
  };

  if (!visible) return null;

  return (
    <div className="upp-overlay">
      <div className="upp-modal">
        <div className="upp-title">🖨️ Select USB Printer</div>
        <p className="upp-sub">Bills and KOTs will print directly to this printer.</p>
        {error && <p className="upp-error">{error}</p>}

        {native ? (
          devices.length === 0 ? (
            <p className="upp-empty">
              No USB device found. Plug the printer into the device and tap Refresh.
            </p>
          ) : (
            <div className="upp-list">
              {devices.map((d) => (
                <button
                  key={`${d.vendorId}-${d.productId}`}
                  className="upp-device"
                  onClick={() => pickNative(d)}
                >
                  <span className="upp-device-name">
                    {d.isPrinter ? '🖨️ ' : '🔌 '}
                    {d.productName || 'USB Device'}
                  </span>
                  <span className="upp-device-meta">
                    {d.manufacturerName || ''} {d.vendorId.toString(16).padStart(4, '0')}:
                    {d.productId.toString(16).padStart(4, '0')}
                  </span>
                </button>
              ))}
            </div>
          )
        ) : (
          <button className="upp-pick" onClick={pickWeb}>
            Choose USB Printer…
          </button>
        )}

        <div className="upp-actions">
          {native && (
            <button className="upp-btn" onClick={refresh}>
              Refresh
            </button>
          )}
          <button className="upp-btn upp-skip" onClick={() => close(true)}>
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsbPrinterPrompt;
